import React, { useState, useEffect } from 'react';

interface UnifiedViewTabProps {
  bookingId: number;
  currency: string;
  payments: any[];
  paymentSummary: any;
}

const UnifiedViewTab: React.FC<UnifiedViewTabProps> = ({ bookingId, currency, payments, paymentSummary }) => {
  const [loading, setLoading] = useState(true);
  const [unified, setUnified] = useState<any>(null);

  useEffect(() => { loadData(); }, [bookingId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem('jwt_token');
      const res = await fetch(`/api/india-ops/${bookingId}/unified`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setUnified(data.data);
    } catch (e) {
      console.error('Error loading unified view:', e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="text-center py-8">Loading unified view...</div>;

  const hasGst = unified?.gst_invoice;
  const gst = unified?.gst_invoice;
  const cols = unified?.india_collections;
  const rems = unified?.remittances;
  const gstLiab = unified?.gst_liability;
  const ic = unified?.intercompany_invoice;

  return (
    <div className="space-y-6">
      {/* All Payments */}
      <div className="border rounded-lg p-4">
        <h4 className="font-semibold text-gray-800 mb-3">💰 All Payments</h4>
        <div className="space-y-1">
          {/* Direct payments to IIW */}
          {payments.filter(p => p.amount > 0).map((p: any, i: number) => (
            <div key={`direct-${i}`} className="flex items-center justify-between bg-gray-50 rounded p-2 text-sm">
              <div className="flex items-center gap-3">
                <span className="text-gray-500">{p.date ? new Date(p.date).toLocaleDateString('en-GB') : '—'}</span>
                <span className="font-medium">{p.currency} {p.amount.toLocaleString()}</span>
                <span className="text-xs text-gray-400">{p.account}</span>
              </div>
              <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">DIRECT</span>
            </div>
          ))}

          {/* India collections */}
          {cols?.items?.map((c: any, i: number) => (
            <div key={`col-${i}`} className="flex items-center justify-between bg-gray-50 rounded p-2 text-sm">
              <div className="flex items-center gap-3">
                <span className="text-gray-500">{new Date(c.date).toLocaleDateString('en-GB')}</span>
                <span className="font-medium">₹{c.amount_inr.toLocaleString('en-IN')}</span>
                <span className="text-xs text-gray-400">{c.payment_method}</span>
              </div>
              <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">VIA INDIA</span>
            </div>
          ))}

          {payments.length === 0 && (!cols?.items || cols.items.length === 0) && (
            <div className="text-center text-gray-400 text-sm py-4">No payments recorded</div>
          )}
        </div>
      </div>

      {/* Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Regular Invoice */}
        <div className="border rounded-lg p-4">
          <h4 className="font-semibold text-gray-800 mb-3">🧾 Regular Invoice ({currency})</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Invoice Total</span>
              <span className="font-medium">{currency} {paymentSummary?.invoice_total?.toLocaleString() || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Direct Payments</span>
              <span className="font-medium">{currency} {paymentSummary?.payments_total?.toLocaleString() || 0}</span>
            </div>
            {hasGst && (
              <div className="flex justify-between">
                <span className="text-gray-500">Via India (FlyRemit)</span>
                <span className="font-medium">${rems?.total_usd?.toLocaleString() || 0}</span>
              </div>
            )}
            <div className="flex justify-between border-t pt-2">
              <span className="font-medium">Balance</span>
              <span className={`font-bold ${(paymentSummary?.balance_due || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {currency} {paymentSummary?.balance_due?.toLocaleString() || 0}
              </span>
            </div>
          </div>
        </div>

        {/* India Operations */}
        {hasGst && (
          <div className="border rounded-lg p-4">
            <h4 className="font-semibold text-gray-800 mb-3">🇮🇳 India Operations</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">GST Invoice Total</span>
                <span className="font-medium">₹{gst.total_inr?.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Collected from Agent</span>
                <span className={`font-medium ${cols?.total_inr >= gst.total_inr ? 'text-green-600' : 'text-orange-600'}`}>
                  ₹{cols?.total_inr?.toLocaleString('en-IN') || 0}
                  {cols?.total_inr >= gst.total_inr && ' ✅'}
                </span>
              </div>
              <div className="border-t pt-2 space-y-1">
                <div className="flex justify-between">
                  <span className="text-gray-500">→ Package (remit to IIW)</span>
                  <span className="font-medium">₹{gst.package_cost_inr?.toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">→ Handling Fee (India keeps)</span>
                  <span className="font-medium text-green-700">₹{gst.handling_fee_inr?.toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">→ GST (to Govt)</span>
                  <span className="font-medium text-blue-700">₹{gst.gst_amount?.toLocaleString('en-IN')}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Remittances + GST Liability */}
      {hasGst && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Remittances */}
          <div className="border rounded-lg p-4">
            <h4 className="font-semibold text-gray-800 mb-3">🔄 Remittances to IIW</h4>
            {rems?.items?.length > 0 ? (
              <div className="space-y-1">
                {rems.items.map((r: any, i: number) => (
                  <div key={i} className="flex items-center justify-between text-sm bg-gray-50 rounded p-2">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">{new Date(r.date).toLocaleDateString('en-GB')}</span>
                      <span className="font-medium">${r.amount_usd.toLocaleString()}</span>
                      {r.ref && <span className="text-xs text-gray-400">{r.ref}</span>}
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${r.status === 'COMPLETED' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{r.status}</span>
                  </div>
                ))}
                <div className="text-right text-sm font-medium mt-1">
                  ${rems.total_usd.toLocaleString()} / ${gst.package_cost_usd?.toLocaleString()}
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-400">No remittances yet</div>
            )}
          </div>

          {/* GST Liability */}
          <div className="border rounded-lg p-4">
            <h4 className="font-semibold text-gray-800 mb-3">🏛️ GST Liability</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">GST Collected</span>
                <span className="font-medium">₹{gstLiab?.collected?.toLocaleString('en-IN') || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Remitted to Govt</span>
                <span className="font-medium text-green-600">₹{gstLiab?.remitted?.toLocaleString('en-IN') || 0}</span>
              </div>
              <div className="flex justify-between border-t pt-2">
                <span className="font-medium">Outstanding</span>
                <span className={`font-bold ${(gstLiab?.outstanding || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  ₹{gstLiab?.outstanding?.toLocaleString('en-IN') || 0}
                  {(gstLiab?.outstanding || 0) <= 0 && ' ✅'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Intercompany Invoice */}
      {ic && (
        <div className="border rounded-lg p-4">
          <h4 className="font-semibold text-gray-800 mb-2">🏢 Intercompany Invoice</h4>
          <div className="flex items-center gap-4 text-sm">
            <span className="font-medium">{ic.invoice_number}</span>
            <span>${ic.amount_usd.toLocaleString()}</span>
            <span className="text-gray-500">{new Date(ic.date).toLocaleDateString('en-GB')}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${ic.status === 'PAID' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>{ic.status}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnifiedViewTab;
